import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  InjectionToken,
  Inject,
  Optional,
  ViewChild,
  ElementRef
} from '@angular/core';
import {ActionsReasonService} from '../../services/actions-reason.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import memoize from 'lodash/memoize';
import {BehaviorSubject, combineLatest, merge, Observable, Subject, of, EMPTY, ReplaySubject, defer} from 'rxjs';
import {FILESCANCODE, FileUploadService} from '../../services/file-upload.service';
import {WithDestroy} from '../../../shared/components/with-destroy/with-destroy.mixin';
import {switchMap, shareReplay, takeUntil, map, delay, finalize} from 'rxjs/operators';
import {UploadLinkModel} from '../../models/upload-link.model';
import {HttpEventType} from '@angular/common/http';
import {BlobStorageUploadJob} from '../../models/file.model';
import {FILE_SIZE_RESTRICTIONS} from '../../../shared/models/upload-file-restrictions.model';
import {FILE_TYPES} from '../../../shared/constants/upload-restrictions.constants';
import {RequestsService} from '../../services/requests.service';
import {FileSizeRestriction} from '../../../shared/models/upload-file-restrictions.model';
import {UtilsService} from '../../../shared/services/utils.service';
import {MessageService} from '../../../shared/services/message-service.service';
import { LoaderService } from '../../../shared/services/loader.service';

export type RequestActionPositions = 'bottom-right' | string;
export interface RequestActionReasonOptions {
  attachmentAvailable: boolean;
  isRequired: boolean;
  actionName?: string;
  showLabel?: boolean;
  fieldLabel?: string;
  label?: string;
  position?: RequestActionPositions;
}
export interface RequestActionReasonContext {
  [k: string]: any;
}
export const REQUEST_ACTION_REASON_OPTIONS = new InjectionToken<RequestActionReasonOptions>('REQUEST_ACTION_REASON_OPTIONS');
export const REQUEST_ACTION_REASON_CONTEXT = new InjectionToken<RequestActionReasonContext>('REQUEST_ACTION_REASON_CONTEXT');

export function doOnSubscribe<T>(onSubscribe: () => void): (source: Observable<T>) => Observable<T> {
  return function inner(source: Observable<T>): Observable<T> {
    return defer(() => {
      onSubscribe();
      return source;
    });
  };
}

export interface FileUploadJob {
  file: File;
  progress: Subject<number>;
  cancelled: Subject<void>;
  link: Observable<UploadLinkModel>;
  uploadedFile: Observable<BlobStorageUploadJob>;
}

@Component({
  selector: 'app-request-action-reason',
  templateUrl: './request-action-reason.component.html',
  styleUrls: ['./request-action-reason.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RequestActionReasonComponent extends WithDestroy() implements OnInit {
  @ViewChild('attachmentInput', {
    read: ElementRef,
    static: false
  })
  attachmentInput: ElementRef<HTMLInputElement>;
  /**
   * @description Action Description
   */
  public actionName: string;
  public showLabel = true;
  public fieldLabel: string;
  public position: string;
  public form: FormGroup;
  public mainButtonTitle: string;
  public isRequired: boolean;
  public fileName: string;
  public isRedConfirmButton: boolean;
  private _attachmentFileJobs: BehaviorSubject<Array<FileUploadJob>> = new BehaviorSubject([]);
  get attachmentFileJobs(): Array<FileUploadJob> {
    return this._attachmentFileJobs.value;
  }
  set attachmentFileJobs(attachmentFileJobs: Array<FileUploadJob>) {
    this._attachmentFileJobs.next(attachmentFileJobs);
  }
  private _excludedFileJobs: BehaviorSubject<Array<FileUploadJob>> = new BehaviorSubject([]);
  get excludedFileJobs(): Array<FileUploadJob> {
    return this._excludedFileJobs.value;
  }
  set excludedFileJobs(excludedFileJobs: Array<FileUploadJob>) {
    this._excludedFileJobs.next(excludedFileJobs);
  }

  fileSize: FileSizeRestriction = FILE_SIZE_RESTRICTIONS.GLOBAL;

  fileType$ = this.requestService.getFileTypes().pipe(
    map(data => data[FILE_TYPES.DEFAULT]),
    takeUntil(this.ngUnsubscribe$)
  );

  private value = '';

  constructor(
    private actionsReasonService: ActionsReasonService,
    private cd: ChangeDetectorRef,
    private fb: FormBuilder,
    private requestService: RequestsService,
    private fileUploadService: FileUploadService,
    private messageService: MessageService,
    private loaderService: LoaderService,
    @Optional() @Inject(REQUEST_ACTION_REASON_OPTIONS) public options: RequestActionReasonOptions,
    @Optional() @Inject(REQUEST_ACTION_REASON_CONTEXT) private context: RequestActionReasonContext
  ) {
    super();
  }

  onAttachmentsChange() {
    const createJob = file => ({
      file,
      progress: null,
      cancelled: null,
      link: null,
      uploadedFile: null
    });
    Array.from(this.attachmentInput.nativeElement.files)
      .filter(f => !UtilsService.isFileFitsBySize(f.size, this.fileSize))
      .forEach(() => {
        this.messageService.showSystemMessage(
          'warning',
          'File upload error',
          `File size exceeds limitation of ${this.fileSize.sizeNumber} ${this.fileSize.sizeType}`
        );
      });
    this.attachmentFileJobs = [
      ...this.attachmentFileJobs,
      ...Array.from(this.attachmentInput.nativeElement.files)
        .filter(f => UtilsService.isFileFitsBySize(f.size, this.fileSize))
        .map(createJob)
    ];

    this.attachmentInput.nativeElement.value = '';
    setTimeout(() => {
      this.cd.markForCheck();
    }, 0);
  }
  removeAttachment(attachmentIndex: number) {
    this.excludedFileJobs = [...this.excludedFileJobs, this.attachmentFileJobs[attachmentIndex]];
    setTimeout(() => {
      this.cd.markForCheck();
    }, 0);
  }

  public ngOnInit(): void {
    this.actionName = this.actionsReasonService.getAction() || this.options?.actionName;
    if(this.options?.showLabel != null) {
      this.showLabel = this.options?.showLabel;
    }
    this.fieldLabel = this.options?.fieldLabel;
    this.position = this.actionsReasonService.getPosition() || this.options?.position;
    this.mainButtonTitle = this.actionsReasonService.getMainButtonTitle();
    this.isRequired = this.actionsReasonService.getIsRequired() || this.options?.isRequired;

    this.form = this.fb.group({
      comment: [this.value]
    });

    if (this.isRequired) {
      this.form.get('comment').setValidators([Validators.required]);
    }

    const makeTimer = (job: FileUploadJob) => {
      job.progress = job.progress || new ReplaySubject<number>(1);
      job.cancelled = job.cancelled || new Subject();
      job.link =
        job.link ||
        this.fileUploadService.requestAttachmentUploadLink(this.context.requestId, 'stage-action-attachment', job.file).pipe(
          doOnSubscribe(() => {
            // Display some progress in case if link process has started
            job.progress.next(1);
          })
        );

      job.uploadedFile =
        job.uploadedFile ||
        job.link.pipe(
          switchMap(uploadLink =>
            this.fileUploadService.uploadFileWithProgress(uploadLink, job.file).pipe(
              switchMap(event => {
                this.loaderService.showLoader();
                if (event.type === HttpEventType.UploadProgress) {
                  const progress = Math.floor((event.loaded / event.total) * 100);
                  job.progress.next(progress);
                }
                this.cd.markForCheck();
                if (event.type === HttpEventType.Response) {
                  job.progress.next(100);
                }
                if (event.type === HttpEventType.Response) {
                  return of(event).pipe(delay(5000), switchMap(() =>
                    this.fileUploadService.scanUploadFile(uploadLink.uploadId, job.file.name).pipe(
                      switchMap(res => {
                        if (res.code === FILESCANCODE.SAFE) {
                          return of({
                            uploadId: uploadLink.uploadId,
                            uploadUrl: uploadLink.uploadUrl,
                            fileName: job.file.name,
                            fileSize: job.file.size
                          });
                        }
                        job.cancelled.next();
                        const indexesToRemove: number[] = [];

                        this.attachmentFileJobs.forEach((element, index) => {
                          if (element.file.name === job.file.name) {
                            indexesToRemove.push(index);
                          }
                        });

                        indexesToRemove.forEach((index) => {
                          this.removeAttachment(index);
                        });

                        console.error(job.file.name);
                        return EMPTY;
                      }))
                  ));
                }
                return EMPTY;
              }),
              finalize(() => this.loaderService.hideLoader())
            )
          ),
          takeUntil(job.cancelled),
          shareReplay({
            bufferSize: 1,
            refCount: false
          })
        );

      return job.uploadedFile;
    };

    combineLatest([this._attachmentFileJobs, this._excludedFileJobs])
      .pipe(
        switchMap(([attachmentJobs, excludedJobs]) => {
          const activeJobs = attachmentJobs.filter(aj => !excludedJobs.includes(aj));
          const deactivatedJobs = attachmentJobs.filter(aj => excludedJobs.includes(aj));
          deactivatedJobs.forEach(job => job.cancelled.next());
          const resolutionJobs = activeJobs.map(makeTimer);
          return merge(...resolutionJobs);
        })
      )
      .subscribe(() => {});
  }
  getScrollbarWidth = memoize(() => {
    // Creating invisible container
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll'; // forcing scrollbar to appear
    (outer.style as any).msOverflowStyle = 'scrollbar'; // needed for WinJS apps
    document.body.appendChild(outer);

    // Creating inner element and placing it in the container
    const inner = document.createElement('div');
    outer.appendChild(inner);

    // Calculating difference between container's full width and the child width
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

    // Removing temporary elements from the DOM
    outer.parentNode.removeChild(outer);

    return scrollbarWidth;
  });

  public onConfirm(): void {
    if (this.form.valid && this.form.controls && this.form.controls.comment) {
      this.actionsReasonService.confirmAction(
        this.form.controls.comment.value,
        Promise.all(
          this.attachmentFileJobs
            .filter(f => !this.excludedFileJobs.includes(f))
            .map(fileJob => fileJob.uploadedFile)
            .map(file => file.toPromise())
        )
      );
    } else {
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        if (control.pristine) {
          control.markAsTouched();
          control.updateValueAndValidity({onlySelf: true, emitEvent: true});
        }
      });
    }

    this.cd.markForCheck();
  }

  public onCancel(): void {
    this.actionsReasonService.cancelAction();
  }

  public getErrorMessage(): string {
    if (this.form.invalid) {
      if (this.form.get('comment').hasError('required') || this.form.get('comment').hasError('notEmpty')) {
        return 'You must enter a value';
      }

      return 'Invalid value';
    }
    return null;
  }
}
